import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  AppBar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Paper,
  Stack,
  Tab,
  Tabs,
  Toolbar,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { useQuery } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import LiveTvIcon from '@mui/icons-material/LiveTv';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import GroupsIcon from '@mui/icons-material/Groups';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PlaceIcon from '@mui/icons-material/Place';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { publicApi } from '../../api/resultsApi';
import { useCompetitionLive } from '../../hooks/useCompetitionLive';
import { ResultsTable } from '../../components/results/ResultsTable';
import { OverallRankingTable } from '../../components/results/OverallRankingTable';
import { PodiumResultsView } from '../../components/results/PodiumResultsView';
import { OverallPodiumView } from '../../components/results/OverallPodiumView';
import { SplitView } from '../../components/results/SplitView';
import { IndividualRankingTable } from '../../components/results/IndividualRankingTable';
import { TeamSumRankingTable } from '../../components/results/TeamSumRankingTable';
import { DvojbojRankingTable } from '../../components/results/DvojbojRankingTable';
import { IndividualEntryRankingTable } from '../../components/results/IndividualEntryRankingTable';
import { PublicDisciplineStartList } from '../../components/startlist/PublicDisciplineStartList';
import { Logo } from '../../components/Logo';
import { AppFooter } from '../../components/AppFooter';
import { PublicPromoCta } from '../../components/PublicPromoCta';
import { PublicTrialNotice } from '../../components/PublicTrialNotice';
import { logoScale } from '../../utils/logoScale';
import { buildDisplayConfig } from '../../utils/displayConfig';
import { useLayoutVisibility } from '../../hooks/useLayoutVisibility';
import { formatDateCz } from '../../utils/dateFormat';

export const PublicCompetitionPage = () => {
  const { shortCode } = useParams<{ shortCode: string }>();
  const [searchParams] = useSearchParams();

  const [categoryIdx, setCategoryIdx] = useState(0);
  const [view, setView] = useState<'discipline' | 'overall' | 'dvojboj'>('discipline');
  const [disciplineIdx, setDisciplineIdx] = useState(0);
  const [followedTeamId] = useState<number | null>(() => {
    const v = localStorage.getItem(`followed-team-${shortCode}`);
    return v ? Number(v) : null;
  });
  const [now, setNow] = useState(() => Date.now());

  useCompetitionLive(shortCode);

  const { data: competition, isLoading, error: detailError } = useQuery({
    queryKey: ['public', shortCode, 'detail'],
    queryFn: () => publicApi.competitionDetail(shortCode!),
    enabled: !!shortCode,
    refetchInterval: 30_000,
    retry: false,
  });

  // Je aktualne pred naplanovanym zverejnenim?
  const publishAtMs = competition?.publishAt ? new Date(competition.publishAt).getTime() : null;
  const beforePublish = !!publishAtMs && publishAtMs > now;

  const { data: visibility } = useLayoutVisibility();

  const config = useMemo(
    () =>
      competition
        ? buildDisplayConfig(competition, searchParams.get('layout'), visibility)
        : null,
    [competition, searchParams, visibility],
  );

  const { data: results = [] } = useQuery({
    queryKey: ['public', shortCode, 'results'],
    queryFn: () => publicApi.results(shortCode!),
    enabled: !!shortCode,
    refetchInterval: (config?.autoRefreshSeconds ?? 15) * 1000,
  });

  const { data: overall = [] } = useQuery({
    queryKey: ['public', shortCode, 'overall'],
    queryFn: () => publicApi.overall(shortCode!),
    enabled: !!shortCode && view === 'overall' && !!competition?.overallRankingVisible,
    refetchInterval: (config?.autoRefreshSeconds ?? 15) * 1000,
  });

  const { data: teams = [] } = useQuery({
    queryKey: ['public', shortCode, 'teams'],
    queryFn: () => publicApi.teams(shortCode!),
    enabled: !!shortCode,
    refetchInterval: 60_000,
  });

  const [disciplineSort, setDisciplineSort] = useState<'RANK' | 'START_NUMBER'>('START_NUMBER');
  const [individualPublicSubTab, setIndividualPublicSubTab] = useState<
    'individual' | 'individual-entries' | 'team-sum' | 'startovka'
  >('individual');

  // Tick kazdou sekundu pro "pred X s" label
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // Bezpecnost: pokud disciplineIdx je mimo rozsah aktualni kategorie
  // (napr. admin smazal disciplinu, refetch prinesl kratsi pole),
  // resetuj na 0.
  useEffect(() => {
    const cat = competition?.categories[categoryIdx];
    if (cat && disciplineIdx >= cat.disciplines.length && cat.disciplines.length > 0) {
      setDisciplineIdx(0);
    }
  }, [competition, categoryIdx, disciplineIdx]);

  const currentCategory = competition?.categories[categoryIdx];
  const currentDiscipline = currentCategory?.disciplines[disciplineIdx];
  const overallForCategory = overall.find((o) => o.categoryId === currentCategory?.id);

  const filteredResults = useMemo(() => {
    if (!currentDiscipline) return [];
    return results.filter((r) => r.disciplineId === currentDiscipline.id);
  }, [results, currentDiscipline]);

  const teamIdsInCategory = useMemo(() => {
    if (!currentCategory) return new Set<number>();
    return new Set(
      results
        .filter((r) => currentCategory.disciplines.some((d) => d.id === r.disciplineId))
        .map((r) => r.teamId),
    );
  }, [results, currentCategory]);


  if (detailError) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
        <Paper sx={{ p: 4, maxWidth: 480, textAlign: 'center' }}>
          <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
            Soutěž nenalezena
          </Typography>
          <Typography color="text.secondary">
            Odkaz neexistuje nebo byla soutěž stažena z veřejné nabídky. Zkontrolujte URL nebo kontaktujte pořadatele.
          </Typography>
        </Paper>
      </Box>
    );
  }

  if (isLoading || !competition || !config) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (beforePublish && publishAtMs) {
    return <PrePublishView competition={competition} publishAtMs={publishAtMs} now={now} />;
  }

  const layout = config.layout;

  const highlightIfInCategory =
    followedTeamId && teamIdsInCategory.has(followedTeamId) ? followedTeamId : null;

  const renderDisciplineResults = () => {
    if (!currentDiscipline) return null;
    if (currentDiscipline.isIndividual) {
      return (
        <Stack spacing={2}>
          <Tabs
            value={individualPublicSubTab}
            onChange={(_, v) => setIndividualPublicSubTab(v as typeof individualPublicSubTab)}
            sx={{ borderBottom: 1, borderColor: 'divider' }}
            variant="scrollable"
            allowScrollButtonsMobile
          >
            <Tab value="individual" label="Pořadí v disciplíně" />
            <Tab value="individual-entries" label="Pořadí jednotlivců (mini-rankings)" />
            {currentDiscipline.teamScoringCount && (
              <Tab value="team-sum" label="Družstvo (součet)" />
            )}
            <Tab value="startovka" label="Startovka" />
          </Tabs>
          {individualPublicSubTab === 'individual' && (
            <IndividualRankingTable discipline={currentDiscipline} />
          )}
          {individualPublicSubTab === 'individual-entries' && (
            <IndividualEntryRankingTable discipline={currentDiscipline} />
          )}
          {individualPublicSubTab === 'team-sum' && currentDiscipline.teamScoringCount && (
            <TeamSumRankingTable discipline={currentDiscipline} />
          )}
          {individualPublicSubTab === 'startovka' && (
            <PublicDisciplineStartList disciplineId={currentDiscipline.id} />
          )}
        </Stack>
      );
    }
    const commonProps = {
      results: filteredResults,
      config,
      highlightTeamId: highlightIfInCategory,
      showAttempt: (currentDiscipline.attemptsCount ?? 1) > 1,
    };
    if (layout === 'PODIUM') {
      return <PodiumResultsView {...commonProps} timeFormat={currentDiscipline.timeFormat} />;
    }
    if (layout === 'SPLIT_VIEW') {
      return (
        <SplitView
          competition={competition}
          config={config}
          results={filteredResults}
          discipline={currentDiscipline}
          overallForCategory={overallForCategory}
          category={currentCategory ?? null}
          highlightTeamId={highlightIfInCategory}
        />
      );
    }
    // CLASSIC
    const teamsInCurrentCategory = currentCategory
      ? teams.filter((t) => t.categoryId === currentCategory.id)
      : [];
    return (
      <ResultsTable
        results={filteredResults}
        showAttempt={commonProps.showAttempt}
        showStartNumber={config.showStartNumber}
        showAttemptDetails={config.showAttemptDetails}
        showLaneDetails={config.showLaneDetails}
        highlightTeamId={highlightIfInCategory}
        teamsInCategory={teamsInCurrentCategory}
        showAllTeams
        sortMode={disciplineSort}
        timeFormat={currentDiscipline.timeFormat}
      />
    );
  };

  const renderOverallResults = () => {
    if (!competition.overallRankingVisible) {
      return (
        <Card sx={{ textAlign: 'center', py: 4, bgcolor: 'background.paper' }}>
          <CardContent>
            <Stack spacing={2} alignItems="center">
              <HourglassTopIcon sx={{ fontSize: 64, color: 'text.disabled' }} />
              <Typography variant="h5" fontWeight={600}>
                Celkové pořadí ještě nezveřejněno
              </Typography>
              <Typography color="text.secondary" sx={{ maxWidth: 480 }}>
                Soutěž ještě nebyla ukončena. Pořadatel zveřejní celkové pořadí po dokončení
                všech disciplín.
              </Typography>
              <Alert severity="info" variant="outlined" sx={{ mt: 1 }}>
                Mezitím sledujte výsledky jednotlivých disciplín v záložce „Disciplína".
              </Alert>
            </Stack>
          </CardContent>
        </Card>
      );
    }
    if (!overallForCategory || !currentCategory) return null;
    if (layout === 'PODIUM') {
      return (
        <OverallPodiumView
          ranking={overallForCategory}
          disciplines={currentCategory.disciplines}
          config={config}
          highlightTeamId={followedTeamId}
        />
      );
    }
    // CLASSIC / SPLIT_VIEW fallback — klasicka tabulka
    return (
      <OverallRankingTable
        ranking={overallForCategory}
        disciplines={currentCategory.disciplines}
        highlightTeamId={followedTeamId}
        showStartNumber={config.showStartNumber}
        showPoints={config.showPoints}
      />
    );
  };

  const renderDvojbojResults = () => {
    if (!currentCategory) return null;
    const dvojbojDiscs = currentCategory.disciplines.filter((d) => d.isDvojbojDisc);
    if (dvojbojDiscs.length < 2) {
      return (
        <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary">
            Tato kategorie neobsahuje dvě nebo více disciplín dvojboje. Pořadí dvojboje není
            definováno.
          </Typography>
        </Paper>
      );
    }
    return (
      <DvojbojRankingTable
        categoryId={currentCategory.id}
        disciplineLabels={dvojbojDiscs.map((d) => `${d.name} P.`)}
      />
    );
  };

  const hasDvojboj =
    !!currentCategory && currentCategory.disciplines.filter((d) => d.isDvojbojDisc).length >= 2;

  return (
    <>
      <Helmet>
        <title>{competition.name} — live výsledky | HasičiOnline.cz</title>
      </Helmet>

      {config.showLogo && (
        <AppBar position="static" elevation={1} sx={{ bgcolor: 'background.paper' }}>
          <Toolbar>
            <Box
              component="a"
              href="https://souteze.hasicionline.cz"
              sx={{ textDecoration: 'none', color: 'inherit', display: 'flex', alignItems: 'center' }}
            >
              <Logo size={48} />
            </Box>
            {config.showOrganizerLogo && competition.publicOrganizerLogoMime && (() => {
              const s = logoScale(competition.publicOrganizerLogoScale);
              return (
                <Box
                  component="img"
                  src={`/api/public/competitions/${competition.shortCode}/organizer-logo`}
                  alt=""
                  onError={(e) => {
                    (e.currentTarget as HTMLImageElement).style.display = 'none';
                  }}
                  sx={{
                    height: { xs: 48 * s, sm: 64 * s },
                    ml: 2,
                    maxWidth: 240 * s,
                    objectFit: 'contain',
                  }}
                />
              );
            })()}
            <Box sx={{ flexGrow: 1 }} />
            {competition.status === 'LIVE' && (
              <Chip
                icon={<LiveTvIcon />}
                label="LIVE"
                color="error"
                size="small"
                sx={{
                  fontWeight: 700,
                  animation: 'pulse 2s infinite',
                  '@keyframes pulse': {
                    '0%, 100%': { opacity: 1 },
                    '50%': { opacity: 0.6 },
                  },
                }}
              />
            )}
            {competition.status === 'FINISHED' && (
              <Chip label="DOKONČENO" color="success" size="small" />
            )}
          </Toolbar>
        </AppBar>
      )}

      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Box sx={{ mb: 3, pb: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Stack spacing={1}>
            <Typography
              variant="h5"
              fontWeight={700}
              sx={{ lineHeight: 1.2 }}
            >
              {competition.name}
            </Typography>
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
            >
            <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                columnGap: 3,
                rowGap: 0.75,
                color: 'text.secondary',
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <CalendarMonthIcon fontSize="small" sx={{ color: 'primary.main', flexShrink: 0 }} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="caption"
                    sx={{ display: 'block', lineHeight: 1, color: 'text.disabled', fontWeight: 600, letterSpacing: 0.4 }}
                  >
                    DATUM
                  </Typography>
                  <Typography variant="body2" fontWeight={600} color="text.primary" noWrap>
                    {formatDateCz(competition.competitionDate)}
                  </Typography>
                </Box>
              </Stack>

              <Stack direction="row" spacing={1} alignItems="center">
                <PlaceIcon fontSize="small" sx={{ color: 'primary.main', flexShrink: 0 }} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="caption"
                    sx={{ display: 'block', lineHeight: 1, color: 'text.disabled', fontWeight: 600, letterSpacing: 0.4 }}
                  >
                    MÍSTO
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    color={competition.location ? 'text.primary' : 'text.disabled'}
                    sx={{ fontStyle: competition.location ? 'normal' : 'italic' }}
                    noWrap
                  >
                    {competition.location || 'nezadáno'}
                  </Typography>
                </Box>
              </Stack>

              {competition.unitName && (
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ gridColumn: { xs: '1', sm: '1 / -1' } }}
                >
                  <GroupsIcon fontSize="small" sx={{ color: 'primary.main', flexShrink: 0 }} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="caption"
                      sx={{ display: 'block', lineHeight: 1, color: 'text.disabled', fontWeight: 600, letterSpacing: 0.4 }}
                    >
                      POŘADATEL
                    </Typography>
                    <Typography variant="body2" fontWeight={600} color="text.primary" noWrap>
                      {competition.unitName}
                    </Typography>
                  </Box>
                </Stack>
              )}
            </Box>
            </Box>
            {competition.publicOrganizerLogoMime && (
              <Box
                component="img"
                src={`/api/public/competitions/${competition.shortCode}/organizer-logo`}
                alt=""
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = 'none';
                }}
                sx={{
                  flexShrink: 0,
                  width: {
                    xs: 64 * logoScale(competition.publicOrganizerLogoScale),
                    sm: 96 * logoScale(competition.publicOrganizerLogoScale),
                  },
                  height: {
                    xs: 64 * logoScale(competition.publicOrganizerLogoScale),
                    sm: 96 * logoScale(competition.publicOrganizerLogoScale),
                  },
                  objectFit: 'contain',
                  borderRadius: 1,
                }}
              />
            )}
            </Stack>
          </Stack>
        </Box>

        {competition.categories.length === 0 && (
          <Typography color="text.secondary">Soutěž zatím nemá nastavené kategorie.</Typography>
        )}

        {competition.categories.length > 0 && (
          <>
            <Tabs
              value={categoryIdx}
              onChange={(_, v) => {
                setCategoryIdx(v);
                setDisciplineIdx(0);
              }}
              variant="fullWidth"
              sx={{ mb: 2 }}
            >
              {competition.categories.map((c) => (
                <Tab key={c.id} label={c.name} sx={{ flex: 1, maxWidth: 'none' }} />
              ))}
            </Tabs>

            {currentCategory && (
              <Stack spacing={1.5} sx={{ mb: 2 }}>
                <Stack spacing={0.5}>
                  {currentCategory.disciplines.map((d, idx) => {
                    const active = view === 'discipline' && idx === disciplineIdx;
                    return (
                      <Button
                        key={d.id}
                        fullWidth
                        variant={active ? 'contained' : 'outlined'}
                        onClick={() => {
                          setView('discipline');
                          setDisciplineIdx(idx);
                        }}
                        sx={{
                          justifyContent: 'flex-start',
                          textAlign: 'left',
                          py: 0.75,
                          fontSize: '0.95rem',
                          fontWeight: active ? 700 : 500,
                          textTransform: 'none',
                        }}
                        startIcon={
                          active ? (
                            <CheckCircleIcon fontSize="small" />
                          ) : (
                            <RadioButtonUncheckedIcon fontSize="small" sx={{ color: 'action.disabled' }} />
                          )
                        }
                      >
                        {d.name}
                      </Button>
                    );
                  })}
                  <Button
                    fullWidth
                    variant={view === 'overall' ? 'contained' : 'outlined'}
                    color={view === 'overall' ? 'warning' : 'primary'}
                    onClick={() => setView('overall')}
                    sx={{
                      justifyContent: 'flex-start',
                      textAlign: 'left',
                      py: 0.75,
                      fontSize: '0.95rem',
                      fontWeight: 700,
                      textTransform: 'none',
                    }}
                    startIcon={<EmojiEventsIcon fontSize="small" />}
                  >
                    Celkové pořadí
                  </Button>
                  {hasDvojboj && (
                    <Button
                      fullWidth
                      variant={view === 'dvojboj' ? 'contained' : 'outlined'}
                      color={view === 'dvojboj' ? 'warning' : 'primary'}
                      onClick={() => setView('dvojboj')}
                      sx={{
                        justifyContent: 'flex-start',
                        textAlign: 'left',
                        py: 0.75,
                        fontSize: '0.95rem',
                        fontWeight: 700,
                        textTransform: 'none',
                      }}
                      startIcon={<EmojiEventsIcon fontSize="small" />}
                    >
                      Pořadí jednotlivců (Dvojboj)
                    </Button>
                  )}
                </Stack>

                {view === 'discipline' && layout === 'CLASSIC' && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography
                      variant="caption"
                      sx={{ color: 'text.secondary', fontWeight: 600, flexShrink: 0 }}
                    >
                      Řadit:
                    </Typography>
                    <Button
                      size="small"
                      variant={disciplineSort === 'START_NUMBER' ? 'contained' : 'outlined'}
                      onClick={() => setDisciplineSort('START_NUMBER')}
                      startIcon={<FormatListNumberedIcon fontSize="small" />}
                      sx={{
                        textTransform: 'none',
                        fontWeight: disciplineSort === 'START_NUMBER' ? 700 : 500,
                        flex: 1,
                        whiteSpace: 'nowrap',
                        minWidth: 0,
                      }}
                    >
                      Startovní č.
                    </Button>
                    <Button
                      size="small"
                      variant={disciplineSort === 'RANK' ? 'contained' : 'outlined'}
                      onClick={() => setDisciplineSort('RANK')}
                      startIcon={<EmojiEventsIcon fontSize="small" />}
                      sx={{
                        textTransform: 'none',
                        fontWeight: disciplineSort === 'RANK' ? 700 : 500,
                        flex: 1,
                        whiteSpace: 'nowrap',
                        minWidth: 0,
                      }}
                    >
                      Pořadí
                    </Button>
                  </Stack>
                )}
              </Stack>
            )}

            <Divider sx={{ mb: 2 }} />

            {view === 'discipline' && renderDisciplineResults()}
            {view === 'overall' && renderOverallResults()}
            {view === 'dvojboj' && renderDvojbojResults()}
          </>
        )}

        {competition.publicDisclaimerText && competition.publicDisclaimerText.trim() && (
          <Paper
            variant="outlined"
            sx={{
              mt: 4,
              p: 2,
              borderColor: 'warning.main',
              bgcolor: (theme) =>
                theme.palette.mode === 'dark'
                  ? 'rgba(255, 167, 38, 0.08)'
                  : 'rgba(255, 167, 38, 0.12)',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                whiteSpace: 'pre-line',
                color: 'text.secondary',
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              {competition.publicDisclaimerText}
            </Typography>
          </Paper>
        )}

        <PublicPromoCta />
        <PublicTrialNotice />
      </Container>
      <AppFooter />
    </>
  );
};

interface PrePublishViewProps {
  competition: {
    name: string;
    location: string | null;
    competitionDate: string;
    unitName: string | null;
  };
  publishAtMs: number;
  now: number;
}

const PrePublishView = ({ competition, publishAtMs, now }: PrePublishViewProps) => {
  const remainingMs = Math.max(0, publishAtMs - now);
  const totalSec = Math.floor(remainingMs / 1000);
  const days = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const minutes = Math.floor((totalSec % 3600) / 60);
  const seconds = totalSec % 60;
  const publishDate = new Date(publishAtMs);
  const publishLabel = publishDate.toLocaleString('cs-CZ', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <>
      <Helmet>
        <title>{competition.name} | HasičiOnline.cz</title>
      </Helmet>
      <AppBar position="static" color="default" elevation={0}>
        <Toolbar sx={{ gap: 2 }}>
          <Box
            component="a"
            href="https://souteze.hasicionline.cz"
            sx={{ textDecoration: 'none', color: 'inherit', display: 'flex', alignItems: 'center' }}
          >
            <Logo size={48} />
          </Box>
        </Toolbar>
      </AppBar>
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Paper sx={{ p: { xs: 3, sm: 5 }, textAlign: 'center' }}>
          <Stack spacing={3} alignItems="center">
            <HourglassTopIcon sx={{ fontSize: 72, color: 'primary.main' }} />
            <Box>
              <Typography variant="overline" color="text.secondary">
                {competition.unitName ?? ''}
              </Typography>
              <Typography variant="h4" fontWeight={800} sx={{ lineHeight: 1.1 }}>
                {competition.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {formatDateCz(competition.competitionDate)}
                {competition.location ? ` • ${competition.location}` : ''}
              </Typography>
            </Box>
            <Divider flexItem />
            <Typography variant="body1" color="text.secondary">
              Výsledky budou dostupné od
            </Typography>
            <Typography variant="h5" fontWeight={700}>
              {publishLabel}
            </Typography>
            <Stack direction="row" spacing={1.5} justifyContent="center">
              {[
                { label: 'dní', value: days },
                { label: 'hod', value: hours },
                { label: 'min', value: minutes },
                { label: 'sek', value: seconds },
              ].map((unit) => (
                <Paper
                  key={unit.label}
                  variant="outlined"
                  sx={{ px: 2, py: 1.5, minWidth: 64, bgcolor: 'background.default' }}
                >
                  <Typography
                    sx={{
                      fontFamily: 'monospace',
                      fontSize: '1.75rem',
                      fontWeight: 800,
                      lineHeight: 1,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {String(unit.value).padStart(2, '0')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {unit.label}
                  </Typography>
                </Paper>
              ))}
            </Stack>
          </Stack>
        </Paper>
      </Container>
      <AppFooter />
    </>
  );
};
